// packages/app/src/App.tsx

import './assests/styles.css';

import catalogFrontendPlugin from '@backstage/plugin-catalog/alpha';
import catalogGraphFrontendPlugin from '@backstage/plugin-catalog-graph/alpha';
import catalogImportFrontendPlugin from '@backstage/plugin-catalog-import/alpha';
import kubernetesFrontendPlugin from '@backstage/plugin-kubernetes/alpha';
import techDocsFrontendPlugin from '@backstage/plugin-techdocs/alpha';
import {
  techDocsReportIssueAddonModule,
  techDocsTextSizeAddonModule,
} from '@backstage/plugin-techdocs-module-addons-contrib/alpha';
import { datadogDashboardPlugin } from '@internal/backstage-plugin-datadog-dashboard';
import readmeFrontendPlugin from '@axis-backstage/plugin-readme/alpha';

import { createApp } from '@backstage/frontend-defaults';
import {
  createFrontendModule,
  ErrorDisplay,
  NotFoundErrorPage,
} from '@backstage/frontend-plugin-api';
import { SwappableComponentBlueprint } from '@backstage/plugin-app-react';

import { appModuleShell } from './app/AppShellModule';
import { appModulePresentation } from './app/AppPresentationModule';
import { appRoutePaths, appRoutePatterns } from './components/Root/navigation';
import {
  DrivelineAppErrorBoundaryFallback,
  DrivelineNotFoundErrorPage,
} from './components/common/DrivelineErrorPage';
import { appModuleDrivelineApis } from './apis/frontendApiExtensions';
import { apiDocsEntityContentModule } from './plugins/apiDocs/apiDocsEntityContentModule';
import { apiDocsPageModule } from './plugins/apiDocs/apiDocsPageModule';
import { apiDocsPlugin } from './plugins/apiDocs/plugin';
import { catalogGraphEntityContentModule } from './plugins/catalogGraph/catalogGraphEntityContentModule';
import { catalogDependenciesEntityContentModule } from './plugins/catalog/catalogDependenciesEntityContentModule';
import { catalogEntityUnregisterModule } from './plugins/catalog/catalogEntityUnregisterModule';
import { catalogOverviewEntityContentModule } from './plugins/catalog/catalogOverviewEntityContentModule';
import { catalogPageModule } from './plugins/catalog/catalogPageModule';
import { catalogImportPageModule } from './plugins/catalogImport/catalogImportPageModule';
import { drivelinePlugin } from './plugins/driveline';
import { gitlabEntityContentModule } from './plugins/gitlab/gitlabEntityContentModule';
import { readmeEntityContentModule } from './plugins/readme/readmeEntityContentModule';
import { techDocsEntityContentModule } from './plugins/techdocs/techDocsEntityContentModule';
import { techDocsFooterClearanceAddonModule } from './plugins/techdocs/techDocsFooterClearanceAddonModule';

const customizedCatalogImportFrontendPlugin =
  catalogImportFrontendPlugin.withOverrides({
    extensions: [
      catalogImportFrontendPlugin
        .getExtension('page:catalog-import')
        .override({ disabled: true }),
    ],
  });

const customizedKubernetesFrontendPlugin =
  kubernetesFrontendPlugin.withOverrides({
    extensions: [
      kubernetesFrontendPlugin
        .getExtension('api:kubernetes')
        .override({ disabled: true }),
      kubernetesFrontendPlugin
        .getExtension('api:kubernetes/auth-providers')
        .override({ disabled: true }),
      kubernetesFrontendPlugin
        .getExtension('api:kubernetes/cluster-link-formatter')
        .override({ disabled: true }),
      kubernetesFrontendPlugin
        .getExtension('api:kubernetes/proxy')
        .override({ disabled: true }),
      kubernetesFrontendPlugin
        .getExtension('page:kubernetes')
        .override({ disabled: true }),
    ],
  });

const customizedTechDocsFrontendPlugin = techDocsFrontendPlugin.withOverrides({
  extensions: [
    techDocsFrontendPlugin.getExtension('page:techdocs/reader').override({
      params: {
        path: appRoutePatterns.techDocsReader,
        noHeader: true,
      },
    }),
    techDocsFrontendPlugin.getExtension('page:techdocs').override({
      params: {
        path: appRoutePaths.docs,
        noHeader: true,
        loader: () =>
          import('./components/techdocs/CustomTechDocsIndexPage').then(
            module => <module.CustomTechDocsIndexPage />,
          ),
      },
    }),
    techDocsFrontendPlugin
      .getExtension('nav-item:techdocs')
      .override({ disabled: true }),
    techDocsFrontendPlugin
      .getExtension('plugin-header-action:techdocs')
      .override({ disabled: true }),
    techDocsFrontendPlugin.getExtension('entity-content:techdocs').override({
      params: {
        path: '/docs',
        title: 'Docs',
      },
    }),
    techDocsFrontendPlugin
      .getExtension('empty-state:techdocs/entity-content')
      .override({ disabled: true }),
  ],
});

const customizedReadmeFrontendPlugin = readmeFrontendPlugin.withOverrides({
  extensions: [
    readmeFrontendPlugin
      .getExtension('entity-card:readme')
      .override({ disabled: true }),
  ],
});

const customizedCatalogGraphFrontendPlugin =
  catalogGraphFrontendPlugin.withOverrides({
    extensions: [
      catalogGraphFrontendPlugin
        .getExtension('entity-card:catalog-graph/relations')
        .override({ disabled: true }),
    ],
  });

const customizedCatalogFrontendPlugin = catalogFrontendPlugin.withOverrides({
  extensions: [
    catalogFrontendPlugin
      .getExtension('api:catalog')
      .override({ disabled: true }),
    catalogFrontendPlugin
      .getExtension('api:catalog/entity-presentation')
      .override({ disabled: true }),
    catalogFrontendPlugin
      .getExtension('api:catalog/starred-entities')
      .override({ disabled: true }),
    catalogFrontendPlugin
      .getExtension('page:catalog')
      .override({ disabled: true }),
    catalogFrontendPlugin
      .getExtension('nav-item:catalog')
      .override({ disabled: true }),
    catalogFrontendPlugin
      .getExtension('entity-context-menu-item:catalog/unregister-entity')
      .override({ disabled: true }),
    catalogFrontendPlugin.getExtension('page:catalog/entity').override({
      params: {
        path: appRoutePatterns.catalogEntity,
        noHeader: true,
      },
    }),
    catalogFrontendPlugin
      .getExtension('entity-content:catalog/overview')
      .override({ disabled: true }),
    catalogFrontendPlugin
      .getExtension('entity-card:catalog/about')
      .override({ disabled: true }),
    catalogFrontendPlugin
      .getExtension('entity-card:catalog/depends-on-components')
      .override({ disabled: true }),
    catalogFrontendPlugin
      .getExtension('entity-card:catalog/depends-on-resources')
      .override({ disabled: true }),
    catalogFrontendPlugin
      .getExtension('entity-card:catalog/has-components')
      .override({ disabled: true }),
    catalogFrontendPlugin
      .getExtension('entity-card:catalog/has-resources')
      .override({ disabled: true }),
    catalogFrontendPlugin
      .getExtension('entity-card:catalog/has-subcomponents')
      .override({ disabled: true }),
    catalogFrontendPlugin
      .getExtension('entity-card:catalog/has-subdomains')
      .override({ disabled: true }),
    catalogFrontendPlugin
      .getExtension('entity-card:catalog/has-systems')
      .override({ disabled: true }),
    catalogFrontendPlugin
      .getExtension('entity-card:catalog/labels')
      .override({ disabled: true }),
    catalogFrontendPlugin
      .getExtension('entity-card:catalog/links')
      .override({ disabled: true }),
  ],
});

const appModuleErrorPages = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SwappableComponentBlueprint.make({
      name: 'driveline-not-found-error-page',
      params: defineParams =>
        defineParams({
          component: NotFoundErrorPage,
          loader: () => DrivelineNotFoundErrorPage,
        }),
    }),
    SwappableComponentBlueprint.make({
      name: 'driveline-error-display',
      params: defineParams =>
        defineParams({
          component: ErrorDisplay,
          loader: () => DrivelineAppErrorBoundaryFallback,
        }),
    }),
  ],
});

const app = createApp({
  features: [
    appModulePresentation,
    appModuleDrivelineApis,
    appModuleErrorPages,
    appModuleShell,
    apiDocsPlugin,
    apiDocsPageModule,
    apiDocsEntityContentModule,
    customizedCatalogImportFrontendPlugin,
    catalogImportPageModule,
    customizedCatalogFrontendPlugin,
    catalogPageModule,
    catalogOverviewEntityContentModule,
    catalogDependenciesEntityContentModule,
    catalogEntityUnregisterModule,
    gitlabEntityContentModule,
    customizedKubernetesFrontendPlugin,
    customizedCatalogGraphFrontendPlugin,
    catalogGraphEntityContentModule,
    customizedReadmeFrontendPlugin,
    readmeEntityContentModule,
    datadogDashboardPlugin,
    drivelinePlugin,
    customizedTechDocsFrontendPlugin,
    techDocsEntityContentModule,
    techDocsFooterClearanceAddonModule,
    techDocsReportIssueAddonModule,
    techDocsTextSizeAddonModule,
  ],
});

export default app.createRoot();
